import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  validateEmail,
  validatePassword,
  validateFullName,
  sanitizeInput,
  checkRateLimit,
} from '../utils/validation';
import '../styles/auth.css';


const RegisterPage = () => {
  const [formData, setFormData] = useState({
    fullName: '', email: '', password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setError('');
    const val = e.target.name === 'fullName'
      ? e.target.value
      : sanitizeInput(e.target.value);
    setFormData((prev) => ({ ...prev, [e.target.name]: val }));

    if (e.target.name === 'password') {
      setPasswordErrors(validatePassword(e.target.value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!checkRateLimit('register_attempts', 3, 60 * 60 * 1000)) {
      setError('Too many registration attempts. Please try again in 1 hour.');
      return;
    }

    if (!validateFullName(formData.fullName)) {
      setError('Please enter a valid full name (letters only, 2–60 characters).');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    const pwdErrors = validatePassword(formData.password);
    if (pwdErrors.length > 0) {
      setError('Password does not meet the requirements below.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error } = await signUp({
      email: formData.email,
      password: formData.password,
      fullName: sanitizeInput(formData.fullName),
    });
    setLoading(false);

    if (error) {
      if (error.message?.includes('already registered')) {
        setError('An account with this email already exists. Please sign in.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } else {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 4000);
    }
  };

  if (success) {
    return (
      <div className="auth-wrapper">
        <div className="auth-bg-pattern" />
        <div className="auth-card auth-success-card">
          <CheckCircle2 size={48} color="#E8471E" />
          <h2 className="auth-title" style={{ marginTop: '1rem' }}>Check your email</h2>
          <p className="auth-subtitle">
            We've sent a confirmation link to <strong>{formData.email}</strong>.
            Click it to activate your account, then sign in.
          </p>
          <p className="auth-subtitle" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
            Redirecting to login in a few seconds…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-bg-pattern" />

      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg width="32" height="28" viewBox="0 0 32 28" fill="none">
              <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
              <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
              <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
              <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
            </svg>
          </div>
          <span className="auth-logo-text">SYNFIN</span>
        </div>

        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start managing your finances smarter</p>

        {error && (
          <div className="auth-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="fullName">Full name</label>
            <input
              id="fullName" name="fullName" type="text"
              autoComplete="name" className="form-input"
              placeholder="Jane Smith"
              value={formData.fullName} onChange={handleChange}
              required maxLength={60}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="email">Email address</label>
            <input
              id="email" name="email" type="email"
              autoComplete="email" className="form-input"
              placeholder="you@example.com"
              value={formData.email} onChange={handleChange}
              required maxLength={254}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <div className="input-with-icon">
              <input
                id="password" name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password" className="form-input"
                placeholder="Create a strong password"
                value={formData.password} onChange={handleChange}
                required maxLength={128}
              />
              <button type="button" className="input-icon-btn"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {formData.password && (
              <ul className="password-hints">
                {['At least 8 characters','At least one uppercase letter','At least one number','At least one special character (!@#$%...)'].map((req) => (
                  <li key={req} className={passwordErrors.includes(req) ? 'hint-fail' : 'hint-pass'}>
                    {passwordErrors.includes(req) ? '✗' : '✓'} {req}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">Confirm password</label>
            <div className="input-with-icon">
              <input
                id="confirmPassword" name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password" className="form-input"
                placeholder="Re-enter your password"
                value={formData.confirmPassword} onChange={handleChange}
                required maxLength={128}
              />
              <button type="button" className="input-icon-btn"
                onClick={() => setShowConfirm(v => !v)}
                aria-label={showConfirm ? 'Hide' : 'Show'}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit"
            className={`btn-primary${loading ? ' btn-loading' : ''}`}
            disabled={loading}>
            {loading ? <span className="btn-spinner" /> : (
              <><UserPlus size={18} /> Create account</>
            )}
          </button>
        </form>

        <p className="auth-footer-text">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Sign in</Link>
        </p>
      </div>

      <p className="auth-legal">
        By creating an account, you agree to SynFin's{' '}
        <a href="/terms" className="auth-link">Terms of Service</a> and{' '}
        <a href="/privacy" className="auth-link">Privacy Policy</a>.
      </p>
    </div>
  );
};

export default RegisterPage;
